/**
 * Discount an admin gives on a plan in the Change Subscription dialog - for clients billed offline. Like a coupon
 * discount, it is taken off the plan price before the transaction is saved, so Amount / TotalAmount (and the invoice)
 * hold what the client actually pays.
 */
export type DiscountType = 'percent' | 'amount';

function toNumber(value: number | string | null | undefined): number {
  const parsed = typeof value === 'number' ? value : parseFloat(value ?? '');
  return Number.isFinite(parsed) ? parsed : 0;
}

/** Why `value` can't be taken off `price`, or null when it can (or no discount is entered). */
export function discountError(price: number, value: number | string | null | undefined, type: DiscountType): string | null {
  const discount = toNumber(value);
  if (discount < 0) {
    return 'Discount cannot be negative.';
  }
  if (type === 'percent' && discount > 100) {
    return 'Discount cannot be more than 100%.';
  }
  if (type === 'amount' && Number.isFinite(price) && discount > price) {
    return 'Discount cannot be more than the price.';
  }
  return null;
}

/** The part of `price` the discount takes off: a percentage of it, or a flat amount. 0 without a discount. */
export function discountAmount(price: number, value: number | string | null | undefined, type: DiscountType): number {
  const discount = Math.max(toNumber(value), 0);
  return type === 'percent' ? (price * discount) / 100 : discount;
}

/**
 * The discount as a percentage of `price`, rounded to 2 decimals - what the transaction records, whichever way it was
 * entered. Null without a discount, or while there is no price to take a flat amount from.
 */
export function discountPercentage(price: number, value: number | string | null | undefined, type: DiscountType): number | null {
  const discount = toNumber(value);
  if (discount <= 0) {
    return null;
  }
  if (type === 'percent') {
    return Math.round(discount * 100) / 100;
  }
  if (!Number.isFinite(price) || price <= 0) {
    return null;
  }
  return Math.round((discount / price) * 10000) / 100;
}

/** `price` less the discount, rounded to 2 decimals and never below 0. */
export function discountedPrice(price: number, value: number | string | null | undefined, type: DiscountType): number {
  const total = price - discountAmount(price, value, type);
  return Math.max(Math.round(total * 100) / 100, 0);
}
